import type { BasketResponse } from "@onestore/api/basket"
import type { JSONPatch } from "@onestore/api/types"
import type {
  LegacyApiErrorsError,
  LegacyApiFormValidationError,
  LegacyApiSingleErrors,
} from "@onestore/onestore-store-common/api"
import { HTTP_STATUS } from "@onestore/onestore-store-common/http"
import type { AxiosError } from "axios"
import api from "~/lib/api"
import type { AddToCartSourceValue } from "~/store/basket/constants"
import type { BasketSuccessAction } from "./basket/actions"
import { BasketActions } from "./basket/constants"
import type { AppDispatch, AppState } from "./reducer"

function handleBasketResponse(
  dispatch: AppDispatch,
  promise: Promise<BasketResponse>,
  basketItemsIds: number[],
  source: AddToCartSourceValue | null
) {
  return promise
    .catch(
      (
        error: AxiosError<
          | LegacyApiSingleErrors
          | LegacyApiErrorsError
          | LegacyApiFormValidationError
        >
      ) => {
        const errorData = api.getNormalizedError(error)
        dispatch({
          type: BasketActions.BASKET_ERROR,
          response_status_code:
            error.status || HTTP_STATUS.INTERNAL_SERVER_ERROR,
          message: "message" in errorData ? errorData.message : "",
          basketItemsIds,
        })
      }
    )
    .then((result) => {
      if (result === undefined) {
        return Promise.reject()
      }

      dispatch({
        type: BasketActions.BASKET_SUCCESS,
        result,
        basketItemsIds,
        source,
      } as BasketSuccessAction)

      return Promise.resolve(result)
    })
}

/**
 * Patches the basket. The result is dispatched as `BASKET_SUCCESS`, which the
 * analytics middleware turns into an `add_to_cart` event.
 *
 * `source` is required - not optional - so the compiler lists every call site
 * whenever a new one appears, instead of letting it default silently. Pass:
 *
 * - a `BasketActionSource` when the patch adds something, i.e. the operations
 *   include an `op: add` (`addBasketItem`, `addBasketResource`,
 *   `triggerBasketItemEvent`). It becomes `add_to_cart_source` in GA4.
 * - `null` when it does not: removals, `op: replace` modifications, clearing
 *   the basket - or when a correct label could only come from the caller.
 *
 * It is analytics-only: it never reaches the API call and no reducer reads it,
 * so it cannot change basket behaviour. See ONESTORE-781.
 */
export function patchBasket(
  operations: JSONPatch[],
  basketItemsIds: number[],
  source: AddToCartSourceValue | null
) {
  return (dispatch: AppDispatch, getState: { (): AppState }) => {
    dispatch({
      type: BasketActions.BASKET,
      basketItemsIds,
    })
    const state = getState()

    return handleBasketResponse(
      dispatch,
      api.basketPatch(state.basket.token, operations),
      basketItemsIds,
      source
    )
  }
}
